/**
 * @fileoverview GPU Hardware Detector & Diagnostics Engine
 * @module MikhmelViewer/GPU
 * 
 * Enterprise-grade GPU detection for medical imaging.
 * Integrates hardware capabilities, VRAM estimation, and adaptive UI constraints.
 *
 * [v2.1 FIXES]:
 *  - Убран preventDefault() на неотменяемом событии change
 *  - _ac никогда не становится null (всегда новый AbortController при destroy)
 *  - showFileLimitWarning: закрытие по клику на фон
 *  - Исправлены оценки VRAM: RTX 4090 = 24GB, RTX 4080 = 16GB
 *  - Apple GPU: учёт deviceMemory (M1/M2: 4-6GB, M1 Pro/Max: 6-8GB)
 *  - getRecommendedBatchSize: fallback 10 для недетектированных устройств
 *  - destroyGPUDetector: сброс _isStyleInjected и очистка стилей из DOM
 *  - _detectWebGL2: освобождение canvas backing store
 *  - hookFileInputs: увеличен таймаут ignoreGpuLimit до 500мс
 */

import { log } from '../utils/logger.js';
// [10.19.1] Отказ и прореживание не молчат и на пути поля ввода — реакции
// общие с путём showDirectoryPicker и живут в helpers (без круга импортов).
import { notifyFolderLoadCancelled, notifyDecimation } from '../utils/helpers.js';
// [10.19.1] Прореженная серия обязана сообщать об этом ЧИСЛОМ, а не только
// тостом при загрузке. Пометка живёт в отдельном файле без импортов.
import { notePendingThinning } from './loadThinning.js';

// ══════════════════════════════════════════════════════════════
// 1. TYPES & JSDOC (Intellisense)
// ══════════════════════════════════════════════════════════════

/**
 * @typedef {Object} GPUTier
 * @property {number} level
 * @property {string} label
 * @property {string} color
 * @property {number} cacheSize
 * @property {string[]} features
 */

/**
 * @typedef {Object} GPUInfo
 * @property {GPUTier} tier
 * @property {string} renderer
 * @property {string} vendor
 * @property {boolean} webgl2
 * @property {boolean} webgpu
 * @property {Object} [webgpuLimits]
 * @property {number} maxTextureSize
 * @property {number} maxRenderBufferSize
 * @property {number} max3DTextureSize
 * @property {boolean} sharedArrayBuffer
 * @property {boolean} isMobile
 * @property {number} cores
 * @property {number} deviceMemory - в ГБ
 * @property {number} vramEstimateMB - Оценка видеопамяти в МБ
 * @property {number} maxSlices - Абсолютный максимум срезов 512x512
 * @property {number} recommendedSlices - Безопасный лимит для MPR/3D
 */

// ══════════════════════════════════════════════════════════════
// 2. CONSTANTS & CONFIGURATION
// ══════════════════════════════════════════════════════════════

/** @type {Record<string, GPUTier>} */
export const GPU_TIERS = Object.freeze({
  LOW:    { level: 0, label: 'LOW',    color: '#ef4444', cacheSize: 512,  features: ['basic'] },
  MEDIUM: { level: 1, label: 'MEDIUM', color: '#eab308', cacheSize: 2048, features: ['basic', 'volume', 'mpr'] },
  HIGH:   { level: 2, label: 'HIGH',   color: '#22c55e', cacheSize: 4096, features: ['basic', 'volume', 'mpr', 'webgpu', '3d', 'ai', 'cinematic'] },
});

const CONFIG = Object.freeze({
  SLICE_BYTES_512: 512 * 512 * 2, // 512KB per 16-bit slice
  VRAM_SAFE_USAGE_RATIO: 0.7,     // Используем 70% VRAM под объемы
  MOBILE_RAM_VRAM_RATIO: 0.35,    // Мобилки отдают ~35% RAM под GPU
  DEFAULT_MAX_3D: 256,
  FALLBACK_VRAM: { DESKTOP: 2048, MOBILE: 512 },
  FALLBACK_BATCH_SIZE: 10,        // Консервативный fallback для недетектированных
});

// ══════════════════════════════════════════════════════════════
// 3. ABORT CONTROLLER (исправлен)
// ══════════════════════════════════════════════════════════════

let _ac = new AbortController();

/**
 * Сброс AbortController. Всегда создаёт новый — _ac никогда не null.
 */
function _resetAC() {
  if (_ac) {
    _ac.abort();
  }
  _ac = new AbortController();
}

// ══════════════════════════════════════════════════════════════
// 4. STATE MANAGEMENT (Singleton)
// ══════════════════════════════════════════════════════════════

/** @type {GPUInfo | null} */
let _cachedGpuInfo = null;
let _isDetecting = false;
let _detectPromise = null;

// ══════════════════════════════════════════════════════════════
// 5. CORE ENGINE
// ══════════════════════════════════════════════════════════════

/**
 * Главный метод определения параметров GPU. Защищен от Race Condition.
 * @returns {Promise<GPUInfo>}
 */
export async function detectGPU() {
  if (_cachedGpuInfo) return _cachedGpuInfo;
  if (_isDetecting) return _detectPromise;

  _isDetecting = true;
  _detectPromise = _executeDetection();

  try {
    _cachedGpuInfo = await _detectPromise;
    return _cachedGpuInfo;
  } catch (e) {
    log.error('[GPU Engine] Detection failed:', e);
    throw e;
  } finally {
    _isDetecting = false;
  }
}

/**
 * @private
 * @returns {Promise<GPUInfo>}
 */
async function _executeDetection() {
  const info = {
    tier: GPU_TIERS.LOW,
    renderer: 'Unknown',
    vendor: 'Unknown',
    webgl2: false,
    webgpu: false,
    webgpuLimits: null,
    maxTextureSize: 0,
    maxRenderBufferSize: 0,
    max3DTextureSize: CONFIG.DEFAULT_MAX_3D,
    sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
    isMobile: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent),
    cores: navigator.hardwareConcurrency || 4,
    deviceMemory: navigator.deviceMemory || 0,
    vramEstimateMB: 0,
    maxSlices: 0,
    recommendedSlices: 0,
  };

  _detectWebGL2(info);
  await _detectWebGPU(info);

  info.vramEstimateMB = _estimateVRAM(info);
  info.tier = _classifyTier(info);

  // Вычисление лимитов для MPR/3D
  const safeVramBytes = info.vramEstimateMB * 1024 * 1024 * CONFIG.VRAM_SAFE_USAGE_RATIO;
  let maxSlicesByVram = Math.floor(safeVramBytes / CONFIG.SLICE_BYTES_512);

  // [10.1.1] Где есть WebGPU — спрашиваем НАСТОЯЩИЙ предел, а не гадаем.
  //
  // Видеопамять выше оценивается по НАЗВАНИЮ карты: в _estimateVRAM лежит
  // таблица на сотню строк. Карты нет в таблице — берётся значение по
  // умолчанию, обычно заниженное, и объём режется там, где резать не нужно.
  // WebGL объём памяти не сообщает в принципе, поэтому таблица и появилась.
  // А WebGPU сообщает предел одного буфера — это не вся память карты, но
  // это ФАКТ, и как потолок для одного тома он честнее догадки по имени.
  //
  // Берём большее из двух: таблица занижает чаще, чем завышает, а лимит
  // буфера — жёсткая граница, за которую всё равно не уйти.
  const wgpuLimitBytes = info.webgpuLimits?.maxBufferSize || 0;
  if (wgpuLimitBytes > 0) {
    const byLimit = Math.floor(wgpuLimitBytes * CONFIG.VRAM_SAFE_USAGE_RATIO / CONFIG.SLICE_BYTES_512);
    if (byLimit > maxSlicesByVram) {
      log.info(`[GPU Engine] Предел WebGPU щедрее таблицы имён: срезов ${maxSlicesByVram} → ${byLimit}`);
      maxSlicesByVram = byLimit;
    }
  }

  info.maxSlices = Math.min(info.max3DTextureSize, maxSlicesByVram);
  info.recommendedSlices = Math.max(1, Math.floor(info.maxSlices * 0.8));

  // Адаптивный Cache Size для мобильных
  if (info.isMobile && info.deviceMemory > 0) {
    const mobileCacheLimit = Math.floor(info.deviceMemory * 1024 * 0.25);
    if (mobileCacheLimit < info.tier.cacheSize) {
      info.tier = { ...info.tier, cacheSize: mobileCacheLimit };
    }
  }

  log.info('[GPU Engine] Hardware scanned:', {
    Renderer: info.renderer.substring(0, 40),
    Tier: info.tier.label,
    VRAM: `${info.vramEstimateMB}MB`,
    MaxSlices: info.recommendedSlices,
    WebGPU: info.webgpu,
  });

  return info;
}

/**
 * @private
 * @param {GPUInfo} info
 */
function _detectWebGL2(info) {
  const canvas = document.createElement('canvas');
  let gl = null;

  try {
    gl = canvas.getContext('webgl2', { 
      powerPreference: 'high-performance',
      failIfMajorPerformanceCaveat: false,
    });
  } catch (e) {
    // WebGL2 не поддерживается
  }

  if (!gl) {
    canvas.width = 0;
    canvas.height = 0;
    return;
  }

  try {
    info.webgl2 = true;
    info.maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    info.maxRenderBufferSize = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE);
    info.max3DTextureSize = gl.getParameter(gl.MAX_3D_TEXTURE_SIZE) || CONFIG.DEFAULT_MAX_3D;

    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (ext) {
      info.renderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL);
      info.vendor = gl.getParameter(ext.UNMASKED_VENDOR_WEBGL);
    }
  } catch (e) {
    log.warn('[GPU Engine] WebGL2 parameter read failed:', e.message);
  } finally {
    // Освобождение контекста
    const loseExt = gl.getExtension('WEBGL_lose_context');
    if (loseExt) {
      try {
        loseExt.loseContext();
      } catch (e) {
        // ignore
      }
    }
    gl = null;
    // Явно освобождаем backing store
    canvas.width = 0;
    canvas.height = 0;
  }
}

/**
 * @private
 * @param {GPUInfo} info
 */
async function _detectWebGPU(info) {
  if (!navigator.gpu) return;

  let adapter = null;
  try {
    adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
    if (!adapter) {
      adapter = await navigator.gpu.requestAdapter();
    }

    if (adapter) {
      info.webgpu = true;
      info.webgpuLimits = {
        maxBufferSize: adapter.limits.maxBufferSize,
        maxTextureDimension3D: adapter.limits.maxTextureDimension3D || 0,
        maxComputeWorkgroupStorageSize: adapter.limits.maxComputeWorkgroupStorageSize || 0,
      };
    } else {
      log.info('[GPU Engine] WebGPU present but adapter denied.');
    }
  } catch (e) {
    log.warn('[GPU Engine] WebGPU request failed:', e.message);
  } finally {
    // Отпускаем ссылку на адаптер (явного destroy нет)
    adapter = null;
  }
}

// ══════════════════════════════════════════════════════════════
// 6. HEURISTICS & CLASSIFICATION
// ══════════════════════════════════════════════════════════════

/**
 * @private
 * @param {GPUInfo} info
 * @returns {number} VRAM в МБ
 */
function _estimateVRAM(info) {
  const r = info.renderer.toLowerCase();

  if (!info.isMobile) {
    // NVIDIA RTX 40 series (исправлены объёмы)
    if (/rtx\s*4090/i.test(r)) return 24576;
    if (/rtx\s*4080\s*super/i.test(r)) return 16384;
    if (/rtx\s*4080/i.test(r)) return 16384;
    if (/rtx\s*4070\s*ti\s*super/i.test(r)) return 16384;
    if (/rtx\s*4070\s*ti/i.test(r)) return 12288;
    if (/rtx\s*4070\s*super/i.test(r)) return 12288;
    if (/rtx\s*4070/i.test(r)) return 12288;
    if (/rtx\s*4060\s*ti/i.test(r)) return 8192;
    if (/rtx\s*4060/i.test(r)) return 8192;
    if (/rtx\s*4050/i.test(r)) return 6144;

    // NVIDIA RTX 30 series
    if (/rtx\s*3090\s*ti/i.test(r)) return 24576;
    if (/rtx\s*3090/i.test(r)) return 24576;
    if (/rtx\s*3080\s*ti/i.test(r)) return 12288;
    if (/rtx\s*3080/i.test(r)) return 10240;
    if (/rtx\s*3070\s*ti/i.test(r)) return 8192;
    if (/rtx\s*3070/i.test(r)) return 8192;
    if (/rtx\s*3060\s*ti/i.test(r)) return 8192;
    if (/rtx\s*3060/i.test(r)) return 12288; // 12GB версия распространённее

    // NVIDIA RTX 20 series
    if (/rtx\s*2080\s*ti/i.test(r)) return 11264;
    if (/rtx\s*2080/i.test(r)) return 8192;
    if (/rtx\s*2070/i.test(r)) return 8192;
    if (/rtx\s*2060/i.test(r)) return 6144;

    // NVIDIA GTX 16/10 series
    if (/gtx\s*1660\s*ti/i.test(r)) return 6144;
    if (/gtx\s*1660/i.test(r)) return 6144;
    if (/gtx\s*1650/i.test(r)) return 4096;
    if (/gtx\s*1080\s*ti/i.test(r)) return 11264;
    if (/gtx\s*1080/i.test(r)) return 8192;
    if (/gtx\s*1070/i.test(r)) return 8192;
    if (/gtx\s*1060/i.test(r)) return 6144;

    // NVIDIA Quadro / RTX A-series (workstation)
    if (/rtx\s*a[5-6]000/i.test(r)) return 24576;
    if (/rtx\s*a[4]000/i.test(r)) return 16384;
    if (/rtx\s*a[2-3]000/i.test(r)) return 12288;
    if (/quadro\s*rtx\s*[68]000/i.test(r)) return 49152;
    if (/quadro\s*rtx\s*[45]000/i.test(r)) return 16384;
    if (/quadro\s*p[56]000/i.test(r)) return 24576;

    // AMD Radeon RX 7000 series
    if (/radeon\s*rx\s*79[05]0/i.test(r)) return 24576;
    if (/radeon\s*rx\s*78[05]0/i.test(r)) return 16384;
    if (/radeon\s*rx\s*77[05]0/i.test(r)) return 12288;
    if (/radeon\s*rx\s*76[05]0/i.test(r)) return 8192;

    // AMD Radeon RX 6000 series
    if (/radeon\s*rx\s*69[05]0/i.test(r)) return 16384;
    if (/radeon\s*rx\s*68[05]0/i.test(r)) return 16384;
    if (/radeon\s*rx\s*67[05]0/i.test(r)) return 12288;
    if (/radeon\s*rx\s*66[05]0/i.test(r)) return 8192;

    // AMD Radeon RX 5000 series
    if (/radeon\s*rx\s*5[7-9]\d{2}/i.test(r)) return 8192;
    if (/radeon\s*rx\s*5[5-6]\d{2}/i.test(r)) return 6144;

    // Intel Arc
    if (/arc\s*a[7]70/i.test(r)) return 16384;
    if (/arc\s*a[5-7]5[0-9]/i.test(r)) return 12288;
    if (/arc\s*a[3]8[0-9]/i.test(r)) return 6144;

    // Intel integrated
    if (/uhd|hd graphics|iris\s*xe/i.test(r)) {
      // Iris Xe / UHD делят системную память
      if (info.deviceMemory >= 16) return 4096;
      if (info.deviceMemory >= 8) return 2048;
      return 1024;
    }

    // Apple Silicon (исправлено)
    if (/apple\s*(m\d|gpu)/i.test(r)) {
      // Unified memory — GPU может использовать значительную часть
      if (info.deviceMemory >= 16) return 8192;  // M1/M2 Pro, Max, Ultra
      if (info.deviceMemory >= 8) return 4096;   // M1/M2 с 8GB
      return 2048;                                // старые iPad/iPhone
    }

    // Fallback: оценка по WebGPU limits
    if (info.webgpuLimits?.maxBufferSize) {
      const maxBufGB = info.webgpuLimits.maxBufferSize / (1024 * 1024 * 1024);
      return Math.min(Math.floor(maxBufGB * 1024 * 2), 16384);
    }

    return CONFIG.FALLBACK_VRAM.DESKTOP;
  }

  // ── Mobile GPUs ──

  if (info.deviceMemory > 0) {
    // Если браузер сообщает deviceMemory — используем его
    const estimatedVram = Math.floor(info.deviceMemory * 1024 * CONFIG.MOBILE_RAM_VRAM_RATIO);
    if (estimatedVram > 512) return estimatedVram;
  }

  // Qualcomm Adreno
  if (/adreno\s*75[0-9]/i.test(r)) return 3072;  // Adreno 750 (Snapdragon 8 Gen 3)
  if (/adreno\s*74[0-9]/i.test(r)) return 2560;  // Adreno 740 (Snapdragon 8 Gen 2)
  if (/adreno\s*73[0-9]/i.test(r)) return 2048;  // Adreno 730 (Snapdragon 8 Gen 1)
  if (/adreno\s*7[0-2]\d/i.test(r)) return 1536;
  if (/adreno\s*6[6-9]\d/i.test(r)) return 1536; // Adreno 660+
  if (/adreno\s*6[4-5]\d/i.test(r)) return 1024;
  if (/adreno\s*6[0-3]\d/i.test(r)) return 768;

  // ARM Mali
  if (/immortalis/i.test(r)) return 3072;
  if (/mali-g7[1-9]\d/i.test(r)) return 2048;    // Mali-G710+
  if (/mali-g[6-7]\d/i.test(r)) return 1024;
  if (/mali-g[5]\d/i.test(r)) return 768;

  // Apple GPU на iOS
  if (/apple\s*gpu/i.test(r)) {
    if (info.deviceMemory >= 6) return 3072;
    if (info.deviceMemory >= 4) return 2048;
    return 1536;
  }

  // Samsung Xclipse (AMD RDNA2)
  if (/xclipse/i.test(r)) return 2048;

  // PowerVR
  if (/powervr/i.test(r)) return 512;

  return CONFIG.FALLBACK_VRAM.MOBILE;
}

/**
 * @private
 * @param {GPUInfo} info
 * @returns {GPUTier}
 */
function _classifyTier(info) {
  if (!info.webgl2) return GPU_TIERS.LOW;

  const r = info.renderer.toLowerCase();
  const vram = info.vramEstimateMB;

  // HIGH Tier Heuristics
  const isHighDesktop = (
    /rtx|radeon\s*rx\s*[5-9]\d{2}|arc\s*a[5-7]|apple\s*m[1-9]/i.test(r) ||
    /quadro\s*rtx|rtx\s*a[2-6]000/i.test(r)
  );
  const isHighMobile = (
    /adreno\s*(6[6-9]\d|7\d{2}|8\d{2})|immortalis|mali-g[7-9]\d{2}|xclipse/i.test(r)
  );

  if (info.webgpu || isHighDesktop || isHighMobile || info.maxTextureSize >= 16384 || vram >= 4096) {
    return GPU_TIERS.HIGH;
  }

  // MEDIUM Tier Heuristics
  const isMediumDesktop = (
    /gtx\s*[6-9]\d{2}|gtx\s*1[0-6]\d{2}|uhd|iris|radeon\s*rx\s*[2-4]\d{2}|radeon\s*vega/i.test(r)
  );
  const isMediumMobile = (
    /adreno\s*[4-5]\d{2}|adreno\s*6[0-5]\d|mali-g[5-6]\d|powervr/i.test(r)
  );

  if (isMediumDesktop || isMediumMobile || info.maxTextureSize >= 8192 || vram >= 2048) {
    return GPU_TIERS.MEDIUM;
  }

  return GPU_TIERS.LOW;
}

// ══════════════════════════════════════════════════════════════
// 7. PUBLIC ACCESSORS
// ══════════════════════════════════════════════════════════════

export const getGPUInfo = () => _cachedGpuInfo;
export const supportsFeature = (feature) => _cachedGpuInfo?.tier.features.includes(feature) ?? false;
export const getCacheSize = () => _cachedGpuInfo?.tier.cacheSize ?? 512;

/**
 * [10.19.1] Сколько ФАЙЛОВ каталога можно взять, не спрашивая.
 *
 * Прежде здесь стоял `recommendedSlices` — предел числа срезов В ОДНОМ ТОМЕ,
 * посчитанный из видеопамяти. Сравнивать с ним СУММУ файлов всего каталога
 * неправильно дважды:
 *
 *  1. Каталог — это несколько серий. Врач приносит семь серий по 50–330
 *     срезов: сумма 1071, а ни одной серии, которая не влезает в видеокарту,
 *     там нет. Вопрос всплывал, а резать было нечего.
 *  2. Опасность «3D/MPR обрушит браузер» УЖЕ закрыта ниже по цепочке и
 *     правильно — пер-серийно: planSliceBudget (sliceBudget.js) прореживает
 *     том при сборке, причём стопка сохраняет все срезы, и режется только то,
 *     что уходит в видеопамять. Спрашивать об этом ещё и на входе — значит
 *     выбрасывать срезы НАВСЕГДА, чтобы предотвратить то, что уже предотвращено.
 *
 * Что действительно ограничено числом файлов — кэш кадров и время чтения.
 * Поэтому предел считается из размера кэша: кадр 512×512 по два байта ≈ 0.5 МБ.
 * Ниже прежнего значения предел не опускаем — оно остаётся нижней границей.
 */
const КАДР_МБ = 0.5;
export function getFolderFileLimit() {
  const поТекстуре = _cachedGpuInfo ? _cachedGpuInfo.recommendedSlices : 500;
  const кэшМБ = _cachedGpuInfo?.tier?.cacheSize ?? 512;
  const поКэшу = Math.floor(кэшМБ / КАДР_МБ);
  return Math.max(поТекстуре, поКэшу);
}
export const getSliceLimit = () => _cachedGpuInfo?.recommendedSlices ?? 500;

/**
 * Рекомендованный размер батча для загрузки/обработки.
 * Адаптируется под количество ядер и тип устройства.
 * @returns {number}
 */
export function getRecommendedBatchSize() {
  if (!_cachedGpuInfo) return CONFIG.FALLBACK_BATCH_SIZE; // исправлено с 20 на 10

  const { isMobile, cores, deviceMemory } = _cachedGpuInfo;

  if (isMobile) {
    const base = Math.min(cores * 2, 15);
    return (deviceMemory > 0 && deviceMemory <= 4) ? Math.min(base, 8) : base;
  }

  return Math.min(cores * 5, 50);
}

// ══════════════════════════════════════════════════════════════
// 8. DIAGNOSTIC UI MODULE
// ══════════════════════════════════════════════════════════════

export const DiagnosticUI = {
  _isStyleInjected: false,

  _injectStyles() {
    if (this._isStyleInjected) return;

    // Удаляем старые стили если есть (HMR)
    const existing = document.getElementById('gpu-diag-enterprise-styles');
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = 'gpu-diag-enterprise-styles';
    style.textContent = `
      .gpu-modal-overlay {
        position: fixed; inset: 0; z-index: 99999;
        background: rgba(0, 0, 0, 0.88);
        backdrop-filter: blur(6px);
        display: flex; align-items: center; justify-content: center;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        animation: gpuFadeIn .25s ease-out;
      }
      @keyframes gpuFadeIn { from { opacity: 0 } to { opacity: 1 } }
      .gpu-modal {
        background: linear-gradient(145deg, #0d1520, #162234);
        border: 1px solid rgba(0, 170, 255, 0.4);
        border-radius: 16px; padding: 20px;
        max-width: 380px; width: 92%;
        box-shadow: 0 0 40px rgba(0, 170, 255, 0.2);
        color: #fff;
      }
      .gpu-modal h2 {
        margin: 0 0 14px; color: #0cf;
        font-size: 17px; text-align: center;
      }
      .gpu-row {
        display: flex; justify-content: space-between;
        padding: 6px 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        font-size: 12px;
      }
      .gpu-lbl { color: #6a8a9a }
      .gpu-val { color: #fff; font-weight: 600 }
      .gpu-val.ok { color: #4f8 }
      .gpu-val.warn { color: #fa0 }
      .gpu-val.bad { color: #f44 }
      .gpu-box {
        background: rgba(0, 170, 255, 0.1);
        border: 1px solid rgba(0, 170, 255, 0.25);
        border-radius: 10px; padding: 14px;
        margin: 14px 0; text-align: center;
      }
      .gpu-box-title {
        color: #0af; font-size: 10px;
        text-transform: uppercase; margin-bottom: 6px;
      }
      .gpu-box-val { color: #fff; font-size: 30px; font-weight: 700 }
      .gpu-btn {
        display: block; width: 100%; padding: 11px;
        border: none; border-radius: 10px;
        font-size: 13px; font-weight: 600;
        cursor: pointer; margin-top: 6px;
        transition: all .15s;
      }
      .gpu-btn-ok { background: linear-gradient(135deg, #0af, #08f); color: #fff }
      .gpu-btn-opt { background: rgba(255, 255, 255, 0.08); color: #bbb }
      .gpu-btn-opt:hover { background: rgba(255, 255, 255, 0.15); color: #fff }
      .gpu-btn-risk { background: rgba(255, 80, 80, 0.15); color: #f88 }
      .gpu-warn-modal { background: linear-gradient(145deg, #1a1008, #2a1810); border-color: #f80 }
    `;
    document.head.appendChild(style);
    this._isStyleInjected = true;
  },

  /**
   * Показать диагностическое окно с информацией о GPU.
   */
  showStartupDiagnostic() {
    const info = _cachedGpuInfo;
    if (!info) {
      log.warn('[GPU UI] Call detectGPU() before showing diagnostics.');
      return;
    }

    this._injectStyles();
    const ov = document.createElement('div');
    ov.className = 'gpu-modal-overlay';

    const vramClass = info.vramEstimateMB >= 4096 ? 'ok' : info.vramEstimateMB >= 2048 ? 'warn' : 'bad';
    const apiClass = info.webgpu ? 'ok' : info.webgl2 ? 'warn' : 'bad';

    ov.innerHTML = `
      <div class="gpu-modal">
        <h2>🔍 Диагностика оборудования</h2>
        <div class="gpu-row"><span class="gpu-lbl">Устройство</span><span class="gpu-val">${info.isMobile ? '📱 Мобильное' : '🖥️ Десктоп'}</span></div>
        <div class="gpu-row"><span class="gpu-lbl">GPU</span><span class="gpu-val">${info.renderer.substring(0, 30)}</span></div>
        <div class="gpu-row"><span class="gpu-lbl">Уровень</span><span class="gpu-val" style="color:${info.tier.color}">${info.tier.label}</span></div>
        <div class="gpu-row"><span class="gpu-lbl">API</span><span class="gpu-val ${apiClass}">${info.webgpu ? 'WebGPU' : info.webgl2 ? 'WebGL2' : 'None'}</span></div>
        <div class="gpu-row"><span class="gpu-lbl">VRAM (оценка)</span><span class="gpu-val ${vramClass}">${info.vramEstimateMB} МБ</span></div>
        <div class="gpu-box">
          <div class="gpu-box-title">Безопасный лимит 3D/MPR</div>
          <span class="gpu-box-val">${info.recommendedSlices}</span><span style="color:#6a8a9a;font-size:13px;margin-left:3px">срезов</span>
        </div>
        <button class="gpu-btn gpu-btn-ok" id="gpu-diag-close">OK</button>
      </div>
    `;

    document.body.appendChild(ov);

    const closeModal = () => {
      ov.style.opacity = '0';
      ov.style.transition = 'opacity 0.3s ease';
      setTimeout(() => ov.remove(), 300);
    };

    // Кнопка закрытия
    const closeBtn = ov.querySelector('#gpu-diag-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal, { once: true });
    }

    // Клик на фон
    ov.addEventListener('click', (e) => {
      if (e.target === ov) closeModal();
    }, { once: true });

    // Auto-close через 10с
    setTimeout(closeModal, 10000);
  },

  /**
   * Показать предупреждение о превышении лимита срезов.
   * @param {number} fileCount - количество файлов
   * @param {File[]} files - исходный список файлов
   * @returns {Promise<File[]|null>} - отфильтрованные файлы или null при отмене
   */
  showFileLimitWarning(fileCount, files) {
    return new Promise(resolve => {
      const limit = getFolderFileLimit();
      const кэшМБ = _cachedGpuInfo?.tier?.cacheSize ?? 512;
      this._injectStyles();

      const halfCount = Math.ceil(fileCount / 2);
      const thirdCount = Math.ceil(fileCount / 3);

      const ov = document.createElement('div');
      ov.className = 'gpu-modal-overlay';
      ov.innerHTML = `
        <div class="gpu-modal gpu-warn-modal">
          <h2>Каталог большой</h2>
          <div class="gpu-row"><span class="gpu-lbl">Файлов в каталоге</span><span class="gpu-val bad">${fileCount}</span></div>
          <div class="gpu-row"><span class="gpu-lbl">Влезает в кэш кадров</span><span class="gpu-val">${limit} (${кэшМБ} МБ)</span></div>
          <!-- [10.19.1] Текст переписан: прежний обещал, что прореживание спасает
               3D/MPR от обрушения. Это неправда — том для 3D/MPR прореживается
               сам, пер-серийно и только для показа (sliceBudget). Здесь речь о
               другом: чтении файлов и кэше кадров. А цена прореживания —
               измеренная: на очаге около 6 мм добавочная ошибка объёма до 20%,
               потому что режется КАЖДАЯ серия, включая тонкосрезную. -->
          <p style="font-size:11.5px; color:#cfe; text-align:left; margin:12px 0; line-height:1.45;">
            Загрузка займёт время, и кадров больше, чем влезает в кэш — часть будет перечитываться.
            <br><b>Прореживание — не для 3D:</b> объём для реформатов программа прореживает сама, по каждой серии
            отдельно и только для показа. Здесь срезы выбрасываются НАСОВСЕМ и у всех серий разом,
            включая тонкосрезную: объём очага и карта разницы получат добавочную ошибку
            (на очаге около 6 мм — до 20%).
          </p>
          <button class="gpu-btn gpu-btn-ok" data-action="force">Загрузить всё целиком (рекомендуется)</button>
          <button class="gpu-btn gpu-btn-opt" data-action="decimate" data-n="2">Каждый 2-й срез (~${halfCount}) — быстрее, но измерения хуже</button>
          <button class="gpu-btn gpu-btn-opt" data-action="decimate" data-n="3">Каждый 3-й срез (~${thirdCount})</button>
          <button class="gpu-btn gpu-btn-opt" data-action="cancel">✕ Отмена</button>
        </div>
      `;

      document.body.appendChild(ov);

      const cleanup = (result) => {
        ov.remove();
        resolve(result);
      };

      // Делегирование кликов
      ov.addEventListener('click', (e) => {
        // Клик на фон — отмена
        if (e.target === ov) {
          cleanup(null);
          return;
        }

        const btn = e.target.closest('button');
        if (!btn) return;

        const action = btn.dataset.action;

        switch (action) {
          case 'cancel':
            cleanup(null);
            break;
          case 'force':
            cleanup(files);
            break;
          case 'decimate': {
            const n = parseInt(btn.dataset.n, 10);
            cleanup(files.filter((_, i) => i % n === 0));
            break;
          }
        }
      });
    });
  }
};

// ══════════════════════════════════════════════════════════════
// 9. DOM INTERCEPTORS
// ══════════════════════════════════════════════════════════════

/**
 * Перехват file input для защиты от загрузки слишком больших серий.
 * Использует capture-фазу для перехвата до React/Vue синтетических событий.
 */
export function hookFileInputs() {
  // Пересоздаём AC чтобы старые слушатели отвалились
  _resetAC();

  document.addEventListener('change', async function(e) {
    const input = e.target;
    if (input.tagName !== 'INPUT' || input.type !== 'file' || input.dataset.ignoreGpuLimit) return;

    const files = Array.from(input.files || []);
    const limit = getFolderFileLimit();

    if (files.length <= limit) return;

    // Блокируем другие обработчики (change не cancelable, preventDefault бесполезен)
    e.stopImmediatePropagation();

    const result = await DiagnosticUI.showFileLimitWarning(files.length, files);

    if (result) {
      // [10.5.8] ЗДЕСЬ БЫЛИ ТЕ САМЫЕ ТРИ МИНУТЫ ТИШИНЫ.
      //
      // Стояло вот что: отобранные файлы складывались обратно в поле ввода
      // через DataTransfer —
      //     const dt = new DataTransfer();
      //     result.forEach(f => dt.items.add(f));
      //     input.files = dt.files;
      // — и по полю заново пускалось событие change, чтобы загрузку подхватил
      // обычный обработчик.
      //
      // Приём законный, но цена его на телефоне неподъёмна. Каждый вызов
      // items.add заставляет браузер заново оформить файл, полученный от
      // системного поставщика хранилища; на две с половиной тысячи файлов это
      // тысячи обращений между процессами, подряд и синхронно. Главный поток
      // занят целиком: ни перерисовки, ни окна ожидания, ни счётчика. Снаружи —
      // намертво зависшая программа сразу после выбора в этом самом окне. На
      // компьютере тот же код стоит доли секунды, поэтому и не замечался.
      //
      // Обходной путь не нужен вовсе: список файлов у нас уже есть, отобранный
      // и готовый. Отдаём его загрузчику напрямую. Поле ввода при этом просто
      // очищаем — оно своё дело сделало.
      // [10.19.1] Предупредить о прореживании и на ЭТОМ пути: поле ввода —
      // единственный путь телефона, и раньше телефон прореживал МОЛЧА.
      if (result.length < files.length) {
        notifyDecimation(result.length, files.length);
        notePendingThinning({ шаг: Math.max(2, Math.round(files.length / Math.max(1, result.length))),
          взято: result.length, всего: files.length });
      }
      const api = (typeof window !== 'undefined') ? window.MikhmelViewer : null;
      if (typeof api?.handleFiles === 'function') {
        input.value = '';
        api.handleFiles(result);
        return;
      }
      // Запасной путь — прежний, на случай если фасад ещё не поднялся.
      const dt = new DataTransfer();
      result.forEach(f => dt.items.add(f));
      input.dataset.ignoreGpuLimit = 'true';
      input.files = dt.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));

      // Снимаем флаг игнора для следующих загрузок
      setTimeout(() => delete input.dataset.ignoreGpuLimit, 500);
    } else {
      // [10.19.1] Пользователь отказался — и отказ больше НЕ МОЛЧИТ.
      //
      // Раньше здесь было одно `input.value = ''`: ни сообщения, ни
      // освобождения окна сравнения, а окно «Читаю список файлов…» с
      // бегущими секундами (его заводит openFolderPicker до выбора папки)
      // оставалось на экране навсегда — гасит его только handleFiles,
      // который при отказе не зовётся. На телефоне, где этот путь
      // единственный, программа выглядела намертво зависшей на 0%.
      input.value = '';
      notifyFolderLoadCancelled();
    }
  }, { capture: true, signal: _ac.signal });
}

/**
 * [10.5.9] Спросить про большую серию — из ЛЮБОГО пути загрузки.
 *
 * Страж-прореживатель до сих пор висел только на событии от поля ввода
 * (hookFileInputs). Как только появился второй путь — обход папки через
 * showDirectoryPicker, который отдаёт файлы загрузчику напрямую, — страж на
 * нём просто не срабатывал: не потому, что его убрали, а потому, что мимо
 * него пошли. Так я и «удалил прореживание», ничего не удаляя.
 *
 * Теперь вопрос задаётся отдельной функцией, и её зовут оба пути. Одно место,
 * одно правило, ни одной копии.
 *
 * @param {File[]} files
 * @returns {Promise<File[]|null>} отобранные файлы, или null если человек отменил
 */
export async function askAboutBigSeries(files) {
  const список = Array.from(files || []);
  if (!список.length) return список;
  const limit = getFolderFileLimit();
  if (список.length <= limit) return список;
  const выбор = await DiagnosticUI.showFileLimitWarning(список.length, список);
  // [10.19.1] Прореживание помечается здесь же: дальше по цепочке шаг уже
  // никому не известен, а число, посчитанное на прореженной серии, обязано
  // сказать об этом врачу само.
  if (выбор && выбор.length && выбор.length < список.length) {
    notePendingThinning({ шаг: Math.max(2, Math.round(список.length / выбор.length)),
      взято: выбор.length, всего: список.length });
  }
  return выбор;
}

/** @deprecated Alias — use DiagnosticUI.showStartupDiagnostic() directly */
export const showStartupDiagnostic = () => DiagnosticUI.showStartupDiagnostic();

/**
 * Полная очистка GPU Detector.
 * Сбрасывает AbortController, удаляет стили, очищает кэш.
 */
export function destroyGPUDetector() {
  // Сбрасываем все слушатели
  _resetAC();

  // Удаляем инжектированные стили
  const style = document.getElementById('gpu-diag-enterprise-styles');
  if (style) style.remove();

  // Сбрасываем флаг инжекции
  DiagnosticUI._isStyleInjected = false;

  // Очищаем кэш детекции
  _cachedGpuInfo = null;
  _isDetecting = false;
  _detectPromise = null;

  log.debug('[GPU Engine] Destroyed. Listeners removed, cache cleared.');
}